import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { API_BASE_URL } from '@synergy/config';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Media, UploadType } from '@synergy/models';
import { PoolButton, PoolDropzone, PoolModal, PoolTypography } from '@synergy/components';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { ReactElement, useState } from 'react';
import { request } from '@synergy/api';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import urljoin from 'url-join';

import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/** Where a position lives: an inquiry position or an order position. */
export type PositionKind = 'purchase-items' | 'purchase-order-items';

interface PositionAttachmentsModalProps {
    kind: PositionKind;
    positionId: number;
    positionName: string;
    media: Media[];
    mediaBaseUrl: string;
    readonly?: boolean;
    onClose: () => void;
    onChange: (media: Media[]) => void;
}

/**
 * Documents and pictures of a single position — drawings, photos, datasheets.
 * They stay with the position and are carried into the follow-up document
 * (key-user brief A3.3).
 */
export const PositionAttachmentsModal = (props: PositionAttachmentsModalProps): ReactElement => {
    const dispatch = useThunkDispatch();
    const [busy, setBusy] = useState(false);
    const [media, setMedia] = useState<Media[]>(props.media);

    const publish = (next: Media[]): void => {
        setMedia(next);
        props.onChange(next);
    };

    const handleDrop = async (files: File[]): Promise<void> => {
        if (0 === files.length) {
            return;
        }

        setBusy(true);

        const data = new FormData();
        files.forEach((file, index) => data.append(`files[${index}]`, file, file.name));
        // Files of a position are internal. What the supplier is to receive
        // goes into the attachments of the document, where the split belongs.
        data.append('type', UploadType.Internal);

        try {
            const response = await request.post(
                urljoin(API_BASE_URL, `/${props.kind}/${props.positionId}/media`),
                data,
                { headers: { 'content-type': 'multipart/form-data' } }
            );

            publish([...media, ...response.data.map(Media.fromPlainObject)]);
            // As everywhere else: stored the moment it is dropped (R-197).
            dispatch(Snackbar.enqueue(PoolNotification.success('app.upload.saved', { count: response.data.length })));
        } catch {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (file: Media): Promise<void> => {
        setBusy(true);

        try {
            await request.delete(urljoin(API_BASE_URL, `/media/${file.id}`));
            publish(media.filter(each => each.id !== file.id));
        } finally {
            setBusy(false);
        }
    };

    return (
        <PoolModal
            isVisible={true}
            handleClose={props.onClose}
            title={<FormattedMessage id="app.positions.attachments.title" values={{ position: props.positionName }} />}
            actionChildren={[
                <PoolButton key="close" styleType="firstLevel" onClick={props.onClose}>
                    <FormattedMessage id="app.common.close" />
                </PoolButton>,
            ]}
        >
            <Stack gap={2}>
                {!props.readonly && (
                    <>
                        <PoolTypography typography="caption" id="app.positions.attachments.internalHint" />
                        <PoolDropzone onDrop={handleDrop} disabled={busy} />
                    </>
                )}

                {0 === media.length ? (
                    <PoolTypography typography="caption" id="app.positions.attachments.empty" />
                ) : (
                    media.map(file => (
                        <Stack key={file.id} direction="row" alignItems="center" gap={1}>
                            <Box
                                component="a"
                                href={urljoin(props.mediaBaseUrl, file.pathname)}
                                target="_blank"
                                rel="noopener"
                                sx={{ flex: 1 }}
                            >
                                {file.originalName}
                            </Box>
                            {!props.readonly && (
                                <Tooltip title={<FormattedMessage id="app.common.delete" />}>
                                    <IconButton color="primary" disabled={busy} onClick={() => handleDelete(file)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    ))
                )}
            </Stack>
        </PoolModal>
    );
};
